import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PaymentIcon from '@mui/icons-material/Payment';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { DataService } from 'src/services/data-service';
import StandalonePaymentDialog from './StandalonePaymentDialog';

interface ReceiptGroupSummary {
  id: string;
  active: boolean;
  refNumber: string;
  description: string;
  groupDate: string;
  receiptCount: number;
  totalAmount: number;
}

const PageHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
  color: 'white',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  boxShadow: theme.shadows[4]
}));

const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4]
  }
}));

const ReceiptsListing: React.FC = () => {
  const navigate = useNavigate();
  const [receiptGroups, setReceiptGroups] = useState<ReceiptGroupSummary[]>([]);
  const [filteredReceiptGroups, setFilteredReceiptGroups] = useState<ReceiptGroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  useEffect(() => {
    fetchReceiptGroups();
  }, []);

  useEffect(() => {
    filterReceiptGroups();
  }, [searchTerm, receiptGroups]);

  const fetchReceiptGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DataService.get('api/ReceiptGroup/summaries');

      if (response.ok) {
        const responseData = await response.json();
        let groupsList: ReceiptGroupSummary[] = [];

        if (responseData.success && responseData.data) {
          groupsList = responseData.data;
        } else if (Array.isArray(responseData)) {
          groupsList = responseData;
        }

        // Sort by group date (newest first)
        groupsList.sort((a, b) => new Date(b.groupDate).getTime() - new Date(a.groupDate).getTime());

        setReceiptGroups(groupsList);
        setFilteredReceiptGroups(groupsList);
      } else {
        throw new Error('Failed to fetch receipt groups');
      }
    } catch (error) {
      console.error('Error fetching receipt groups:', error);
      setError(error instanceof Error ? error.message : 'Failed to load receipt groups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterReceiptGroups = () => {
    if (!searchTerm.trim()) {
      setFilteredReceiptGroups(receiptGroups);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = receiptGroups.filter(group =>
      group.refNumber?.toLowerCase().includes(term) ||
      group.description?.toLowerCase().includes(term) ||
      group.totalAmount.toString().includes(term)
    );
    setFilteredReceiptGroups(filtered);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateStats = () => {
    const totalAmount = filteredReceiptGroups.reduce((sum, group) => sum + group.totalAmount, 0);
    const totalReceiptsCount = filteredReceiptGroups.reduce((sum, group) => sum + group.receiptCount, 0);

    return {
      totalGroups: filteredReceiptGroups.length,
      totalAmount,
      totalReceipts: totalReceiptsCount
    };
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const stats = calculateStats();

  return (
    <Container maxWidth="xl" sx={{ py: 1, px: 2 }}>
      {/* Page Header */}
      <PageHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold">
                Receipts
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, mt: 0.5 }}>
                View all receipts and payment history
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setPaymentDialogOpen(true)}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            Create Manual Receipt
          </Button>
        </Box>
      </PageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptIcon sx={{ fontSize: 32, color: 'success.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {stats.totalGroups}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receipts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoneyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {stats.totalAmount.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon sx={{ fontSize: 32, color: 'info.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="info.main">
                    {stats.totalReceipts}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Receipts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="secondary.main">
                    {receiptGroups.length > 0 ? formatDate(receiptGroups[0].groupDate).split(',')[0] : 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Latest Receipts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Box sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          placeholder="Search by reference number, description, or amount..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ bgcolor: 'white' }}
        />
      </Box>

      {/* Receipt Groups Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'success.main', color: 'white', p: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            All Receipt ({filteredReceiptGroups.length})
          </Typography>
        </Box>

        <TableContainer>
          {filteredReceiptGroups.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
              <ReceiptIcon sx={{ fontSize: 60, color: 'grey.300', mb: 1 }} />
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {searchTerm ? 'No receipts found' : 'No receipts available'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {searchTerm ? 'Try adjusting your search criteria' : 'Receipts will appear here once payments are processed'}
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Reference Number</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Receipt Date</strong></TableCell>
                  {/* <TableCell><strong>Payment Count</strong></TableCell> */}
                  <TableCell><strong>Total Amount</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReceiptGroups.map((group, index) => (
                  <TableRow
                    key={group.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/receipt-groups/${group.refNumber}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600" color="primary">
                        {group.refNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {group.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {formatDate(group.groupDate)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    {/* <TableCell>
                      <Chip
                        label={group.receiptCount}
                        size="small"
                        color="info"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell> */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AttachMoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="body2" fontWeight="600" color="success.main">
                          {group.totalAmount.toLocaleString()}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/receipt-groups/${group.refNumber}`);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      {/* Standalone Payment Dialog */}
      <StandalonePaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        onSuccess={() => {
          setPaymentDialogOpen(false);
          fetchReceiptGroups();
        }}
      />
    </Container>
  );
};

export default ReceiptsListing;
